export type KnowledgeOperationType =
    | 'website_rescan'
    | 'website_full_refresh'
    | 'website_selected_refresh'
    | 'website_policy_change'
    | 'document_loader_retrain'
    | 'knowledge_mode_migration';

export type KnowledgeSourceScope = 'scrape' | 'managed' | 'knowledge_faq' | 'all';

export type KnowledgeOperationStatus =
    | 'queued'
    | 'running'
    | 'succeeded'
    | 'partial_failed'
    | 'failed'
    | 'cancelling'
    | 'cancelled';

export type KnowledgeOperationOutcome =
    | 'changed'
    | 'no_changes'
    | 'job_succeeded'
    | 'partial_success'
    | 'completed'
    | 'cancelled';

export type KnowledgeOperationListStatus = 'active' | 'terminal' | 'all';

export type KnowledgeWorkItemStatus =
    | 'pending'
    | 'dispatched'
    | 'running'
    | 'retry_wait'
    | 'succeeded'
    | 'skipped'
    | 'failed'
    | 'cancelled';

export type KnowledgeProgressScope = 'website' | 'managed' | 'knowledge_faq';
export type KnowledgeProgressOwner = 'knowledge_operation' | 'legacy';

export interface KnowledgeOperation {
    id: string;
    site_id: string;
    site_domain: string;
    environment: string;
    operation_type: KnowledgeOperationType;
    source_scope: KnowledgeSourceScope;
    status: KnowledgeOperationStatus;
    current_phase: string | null;
    outcome: KnowledgeOperationOutcome | null;
    progress_percent: number;
    // Document counters, only set while current_phase is document_loader_job (the slow retrain phase).
    loader_processed: number | null;
    loader_total: number | null;
    loader_started_at: string | null;
    // Sub-phase inside the loader job. The loader can sit on the env-wide mutex
    // for minutes, which otherwise reads as a stuck "Refreshing agent knowledge".
    loader_phase: string | null;
    progress: Record<string, unknown>;
    error_summary: string | null;
    retry_of_operation_id: string | null;
    crawl_run_id: string | null;
    created_at: string;
    updated_at: string;
    completed_at: string | null;
}

export interface KnowledgeWorkItem {
    id: string;
    operation_id: string;
    stage: string;
    document_id: string | null;
    group_key: string | null;
    status: KnowledgeWorkItemStatus;
    outcome: string | null;
    attempt_count: number;
    max_attempts: number;
    last_error_code: string | null;
    last_error_message: string | null;
    subject: Record<string, unknown>;
    created_at: string;
    updated_at: string;
}

const REDIRECTED_OUTSIDE =
    'This page redirects outside your synced websites, so Rose cannot read it. Retrying will not help: the website it redirects to must be added first (contact support@userose.ai).';

// The API replaces every failure message with fixed text, so the code is the only
// way to say why. Codes not listed keep that fixed text.
const WORK_ITEM_FAILURE_REASONS: Record<string, string> = {
    site_url_outside_roots: REDIRECTED_OUTSIDE,
    canonical_host_not_allowed: REDIRECTED_OUTSIDE,
    website_redirects_elsewhere:
        'This website redirects to another domain, so Rose cannot read it. Add the domain it redirects to as a website, then retire this one (contact support@userose.ai).',
};

export function workItemFailureReason(item: KnowledgeWorkItem): string {
    return (
        (item.last_error_code ? WORK_ITEM_FAILURE_REASONS[item.last_error_code] : undefined) ??
        item.last_error_message ??
        item.last_error_code ??
        'This item could not be refreshed.'
    );
}

/**
 * Whether retrying could change anything. A redirect failure replays unchanged: the
 * retry reuses the operation's frozen roots. Other permanent failures stay retryable
 * by hand, since an operator may have fixed their cause.
 */
export function canRetryFailedItems(items: KnowledgeWorkItem[], hasMore: boolean): boolean {
    return (
        hasMore ||
        items.length === 0 ||
        items.some(
            (item) => !item.last_error_code || !(item.last_error_code in WORK_ITEM_FAILURE_REASONS)
        )
    );
}

interface KnowledgeOperationCommandBase {
    workspace_id: string;
    site_domain?: string;
    input?: Record<string, unknown>;
}

export interface WebsiteFullRefreshCommand extends KnowledgeOperationCommandBase {
    type: 'website_full_refresh';
    source_scope?: 'scrape';
    target_url: string;
    idempotency_key: string;
}

export interface WebsiteSelectedRefreshCommand extends KnowledgeOperationCommandBase {
    type: 'website_selected_refresh';
    source_scope?: 'scrape';
    document_ids: string[];
    expected_snapshot_ids?: Record<string, string>;
    force_fetch?: boolean;
}

export interface WebsitePolicyChangeCommand extends KnowledgeOperationCommandBase {
    type: 'website_policy_change';
    source_scope?: 'scrape';
    document_ids: string[];
    input: Record<string, unknown>;
    idempotency_key: string;
}

export interface DocumentLoaderRetrainCommand extends KnowledgeOperationCommandBase {
    type: 'document_loader_retrain';
    source_scope: 'managed' | 'knowledge_faq';
    force?: boolean;
    idempotency_key: string;
}

export type CreateKnowledgeOperationCommand =
    | WebsiteFullRefreshCommand
    | WebsiteSelectedRefreshCommand
    | WebsitePolicyChangeCommand
    | DocumentLoaderRetrainCommand;

export interface KnowledgeOperationCreated {
    operation_id: string;
    status: KnowledgeOperationStatus;
    status_url: string;
    deduplicated: boolean;
}

export interface KnowledgeOperationList {
    items: KnowledgeOperation[];
    next_cursor: string | null;
}

export interface KnowledgeWorkItemList {
    items: KnowledgeWorkItem[];
    next_cursor: string | null;
}

export interface KnowledgeOperationTrackHint {
    workspaceId: string;
    operationType: KnowledgeOperationType;
    sourceScope: KnowledgeSourceScope;
}

export const ACTIVE_KNOWLEDGE_OPERATION_STATUSES = new Set<KnowledgeOperationStatus>([
    'queued',
    'running',
    'cancelling',
]);

const WEBSITE_INGESTION_OPERATION_TYPES = new Set<KnowledgeOperationType>([
    'website_full_refresh',
    'website_selected_refresh',
    'website_policy_change',
]);

// A scrape-scoped operation that pushes website pages into the knowledge base.
// The newest completed one marks when the live KB last matched the selection.
export function isWebsiteIngestionOperation(
    operation: Pick<KnowledgeOperation, 'operation_type' | 'source_scope'>
): boolean {
    return (
        operation.source_scope === 'scrape' &&
        WEBSITE_INGESTION_OPERATION_TYPES.has(operation.operation_type)
    );
}

// Everything that is not a loader retrain reports under the website scope,
// including a knowledge_mode_migration: it rebuilds the whole corpus, so owning
// that scope is what keeps the website surface from starting a second ingest.
export function progressScopeForOperation(
    operation: Pick<KnowledgeOperation, 'operation_type' | 'source_scope'>
): KnowledgeProgressScope {
    if (operation.operation_type !== 'document_loader_retrain') return 'website';
    return operation.source_scope === 'knowledge_faq' ? 'knowledge_faq' : 'managed';
}

export function progressScopeForHint(hint: KnowledgeOperationTrackHint): KnowledgeProgressScope {
    if (hint.operationType !== 'document_loader_retrain') return 'website';
    return hint.sourceScope === 'knowledge_faq' ? 'knowledge_faq' : 'managed';
}

// Post-ingest knowledge-graph totals for the tenant. The backend surfaces these
// on the operation result once the document-loader phase completes.
export interface KnowledgeGraphCounts {
    entities?: number;
    relationships?: number;
    chunks?: number;
}

// Work-item counters for one stage. The admin API sends `progress.phases` as a
// list of these, one per stage the operation has work items in.
export interface KnowledgePhaseCounts {
    name: string;
    total: number;
    pending: number;
    active: number;
    succeeded: number;
    skipped: number;
    failed: number;
    cancelled: number;
}

export function phaseCountsForOperation(
    operation: KnowledgeOperation,
    phase: string
): KnowledgePhaseCounts | null {
    const phases = (operation.progress as { phases?: KnowledgePhaseCounts[] })?.phases;
    if (!Array.isArray(phases)) return null;
    return phases.find((counts) => counts?.name === phase) ?? null;
}

export function graphCountsForOperation(
    operation: KnowledgeOperation
): KnowledgeGraphCounts | null {
    const result = (operation.progress as { result?: { graph_counts?: KnowledgeGraphCounts } })
        ?.result;
    const counts = result?.graph_counts;
    if (!counts) return null;
    const hasAny = counts.entities != null || counts.relationships != null || counts.chunks != null;
    return hasAny ? counts : null;
}
